/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Client-side logger.
 *
 * Buffers log entries in memory and ships them to the backend in batches.
 * Designed to never throw - any logging failure is swallowed silently so it
 * can't break the app.
 *
 * Endpoint: POST {VUE_APP_API_URL}/v2/client-logs  body: { logs: [...] }
 */

import { getDashboardApiUrl } from "@/lib/runtime-url-resolver";

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
  sessionId: string;
  userId: string | null;
  timestamp: string;
  level: LogLevel;
  event: string;
  data: Record<string, unknown>;
  url: string;
  userAgent: string;
}

const FLUSH_INTERVAL_MS = 10_000;
const MAX_BUFFER_SIZE = 20;
const MAX_BATCH_SIZE = 100;

function genId(): string {
  try {
    const c = crypto as unknown as { randomUUID?: () => string };
    if (typeof c?.randomUUID === "function") {
      return c.randomUUID();
    }
  } catch {
    // ignore
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const sessionId = genId();
let buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let getUserIdFn: (() => string | null) | null = null;

function getEndpoint(): string {
  const base = getDashboardApiUrl() || "";
  return `${base.replace(/\/$/, "")}/v2/client-logs`;
}

function safeUserId(): string | null {
  try {
    return getUserIdFn ? getUserIdFn() : null;
  } catch {
    return null;
  }
}

function buildEntry(
  level: LogLevel,
  event: string,
  data: Record<string, unknown> = {}
): LogEntry {
  return {
    sessionId,
    userId: safeUserId(),
    timestamp: new Date().toISOString(),
    level,
    event,
    data,
    url: typeof window !== "undefined" ? window.location.href : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  };
}

async function ship(entries: LogEntry[], useBeacon = false): Promise<void> {
  if (!entries.length) return;
  const payload = JSON.stringify({ logs: entries.slice(0, MAX_BATCH_SIZE) });
  const url = getEndpoint();
  try {
    if (
      useBeacon &&
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    });
  } catch {
    // swallow - logging must never break the app
  }
}

export function flushLogs(useBeacon = false): void {
  if (buffer.length === 0) return;
  const toShip = buffer;
  buffer = [];
  void ship(toShip, useBeacon);
}

export function log(
  level: LogLevel,
  event: string,
  data: Record<string, unknown> = {}
): void {
  try {
    buffer.push(buildEntry(level, event, data));
    if (level === "error" || buffer.length >= MAX_BUFFER_SIZE) {
      flushLogs(false);
    }
  } catch {
    // swallow
  }
}

export function setUserIdResolver(fn: () => string | null): void {
  getUserIdFn = fn;
}

export function getSessionId(): string {
  return sessionId;
}

/**
 * Initialise periodic flushing and beforeunload beacon. Safe to call once at
 * app startup. No-op in non-browser environments.
 */
export function initLogger(): void {
  if (typeof window === "undefined") return;
  if (flushTimer) return;
  flushTimer = setInterval(() => flushLogs(false), FLUSH_INTERVAL_MS);
  window.addEventListener("beforeunload", () => flushLogs(true));
  window.addEventListener("pagehide", () => flushLogs(true));
}

export const logger = {
  log,
  flush: flushLogs,
  debug: (event: string, data?: Record<string, unknown>) => log("debug", event, data),
  info: (event: string, data?: Record<string, unknown>) => log("info", event, data),
  warn: (event: string, data?: Record<string, unknown>) => log("warn", event, data),
  error: (event: string, data?: Record<string, unknown>) => log("error", event, data),
};
